import { Injectable, NgZone } from '@angular/core';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { SnackbarService } from './snackbar.service';
import { MatDialog } from '@angular/material/dialog';
import { SessionTimeoutDialogComponent } from '../../shared/session-timeout-dialog/session-timeout-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class SessionTimeoutService {
  private readonly TIMEOUT_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
  private readonly WARNING_BEFORE_TIMEOUT = 30 * 1000; // Show warning 30 seconds before timeout

  private lastActivityTime: number = Date.now();
  private checkInterval: any = null;
  private isAuthenticated = false;
  private dialogRef: any = null;
  private isWarningDisplayed = false;
  private isTabActive = true;
  private isLoggingOut = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private snackbarService: SnackbarService,
    private ngZone: NgZone,
    private dialog: MatDialog
  ) {
    // Subscribe to authentication state changes
    this.authService.isLoggedIn$.subscribe((isLoggedIn) => {
      this.isAuthenticated = isLoggedIn;
      if (isLoggedIn) {
        this.isLoggingOut = false;
        this.startTracking();
      } else {
        this.stopTracking();
      }
    });
  }

  /**
   * Initialize the session timeout tracking
   */
  init(): void {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // Track user activity
      const events = ['click', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      events.forEach((event) => {
        window.addEventListener(event, () => this.handleUserActivity());
      });

      // Track tab visibility
      document.addEventListener('visibilitychange', () =>
        this.handleVisibilityChange()
      );

      // Start tracking if already authenticated
      if (this.isAuthenticated) {
        this.startTracking();
      }
    }
  }

  /**
   * Start tracking user activity
   */
  private startTracking(): void {
    // Reset last activity time
    this.lastActivityTime = Date.now();
    this.isTabActive = document.visibilityState === 'visible';

    // Clear any existing interval
    this.stopTracking();

    // Start a new check interval (runs every second)
    this.checkInterval = setInterval(() => {
      this.checkSessionStatus();
    }, 1000);
  }

  /**
   * Stop tracking user activity
   */
  private stopTracking(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
      this.isWarningDisplayed = false;
    }
  }

  /**
   * Handle user activity
   */
  private handleUserActivity(): void {
    // Only update activity time if the warning dialog is not displayed
    if (!this.isWarningDisplayed) {
      this.lastActivityTime = Date.now();
    }
  }

  /**
   * Handle tab visibility changes
   */
  private handleVisibilityChange(): void {
    this.isTabActive = document.visibilityState === 'visible';

    if (this.isTabActive) {
      // When tab becomes active, check session status immediately
      this.checkSessionStatus();
    }
  }

  /**
   * Check session status based on inactivity time
   */
  private checkSessionStatus(): void {
    if (!this.isAuthenticated || this.isLoggingOut) return;

    const now = Date.now();
    const inactiveTime = now - this.lastActivityTime;

    // If user has been inactive for too long, log them out
    if (inactiveTime >= this.TIMEOUT_DURATION) {
      this.ngZone.run(() => {
        this.handleTimeout();
      });
      return;
    }

    // If user has been inactive long enough to show warning and warning is not already displayed
    if (
      inactiveTime >= this.TIMEOUT_DURATION - this.WARNING_BEFORE_TIMEOUT &&
      !this.isWarningDisplayed &&
      this.isTabActive
    ) {
      this.ngZone.run(() => {
        this.showWarningDialog();
      });
    }
  }

  /**
   * Show warning dialog before timeout
   */
  private showWarningDialog(): void {
    if (!this.isAuthenticated || this.isWarningDisplayed || this.isLoggingOut)
      return;

    // Calculate remaining time in seconds (rounded to nearest second)
    const remainingTime = Math.round(
      (this.TIMEOUT_DURATION - (Date.now() - this.lastActivityTime)) / 1000
    );

    // Only show dialog if there's actually time remaining
    if (remainingTime <= 0) {
      this.handleTimeout();
      return;
    }

    // Set flag that warning is displayed
    this.isWarningDisplayed = true;

    // Open dialog
    this.dialogRef = this.dialog.open(SessionTimeoutDialogComponent, {
      disableClose: true,
      data: { remainingTime },
      panelClass: 'common-dialog-style',
      backdropClass: 'dialogBackdrop',
    });

    // Handle dialog close
    this.dialogRef.afterClosed().subscribe((result: boolean) => {
      this.isWarningDisplayed = false;
      this.dialogRef = null;

      if (result === true) {
        // User wants to continue session
        this.lastActivityTime = Date.now();
      } else {
        // User chose to logout
        this.logoutUser('Logout successful');
      }
    });
  }

  /**
   * Handle timeout when user is inactive
   */
  private handleTimeout(): void {
    if (!this.isAuthenticated || this.isLoggingOut) return;

    // Close the warning dialog if it's still open
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
      this.isWarningDisplayed = false;
    }

    this.logoutUser('You have been logged out due to inactivity.');
  }

  /**
   * Centralized method to handle logout
   */
  private logoutUser(message: string): void {
    if (this.isLoggingOut) return;

    this.isLoggingOut = true;
    this.snackbarService.showSuccess(message);
    this.authService.logout().subscribe({
      next: () => {
        // Logout successful
      },
      error: () => {
        // Reset flag in case of error
        this.isLoggingOut = false;
      },
    });
  }
}
